
import type { PropType } from 'vue'
import { defineComponent } from 'vue'
import { UiTooltip } from '../keen-ui'
import { EpbButton } from '../shared/epb-button'
import { EpbPill } from './epb-pill'

export interface Suggestion {
  value: string
  label?: string
}

export default defineComponent({
  name: 'SearchBox',
  components: {
    EpbButton,
    EpbPill,
    UiTooltip,
  },
  props: {
    query: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    strictMatching: {
      type: Boolean,
      default: undefined,
    },
    size: {
      type: String, // 'medium' | 'large'
      default: 'medium',
    },
    suggestions: {
      type: Array as PropType<Array<Suggestion>>,
      default: () => [],
    },
    disabled: {
      type: Boolean,
    },
    infoPillLabel: { type: String },
    infoPillBackgroundColor: {
      type: String,
      default: () => 'neutral',
    },
  },
  data(): Record<string, any> {
    return {
      baseClass: 'epb-search-box',
      focused: undefined,
      inputQuery: undefined,
    }
  },
  computed: {
    classes(): Array<string | Record<string, boolean>> {
      return [
        this.baseClass,
        `${this.baseClass}--${this.size}`,
        {
          [`${this.baseClass}--disabled`]: this.disabled,
          [`${this.baseClass}--suggestions-visible`]: this.suggestionsVisible,
        },
      ]
    },
    showStrictMatching(): boolean {
      return this.strictMatching !== undefined
    },
    suggestionsVisible(): boolean {
      return this.suggestions.length && this.focused
    },
  },
  watch: {
    query: {
      handler(value: string): void {
        this.inputQuery = value
      },
      immediate: true,
    },
    inputQuery: {
      handler(value: string): void {
        this.$emit('input', value)
      },
      immediate: true,
    },
  },
  methods: {
    focus(): void {
      this.focused = true
    },
    blur(): void {
      setTimeout(() => (this.focused = false), 100)
    },
    setQuery(value: string): void {
      this.inputQuery = value
      this.$emit('setQuery', value)
    },
    updateQuery(value: string): void {
      this.inputQuery = value
      this.$emit('updateQuery', value)
    },
    clearQuery(): void {
      this.inputQuery = undefined
      this.$emit('clearQuery')
    },
    toggleStrictMatching(): void {
      const query = this.$refs.input.value
      this.updateQuery(query)
      this.$emit('updateStrictMatching', !this.strictMatching)
    },
  },
})
